import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '@bumble/app/hooks/use-api';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { Viewer } from '@react-pdf-viewer/core';
import { toolbarPlugin } from '@react-pdf-viewer/toolbar';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/toolbar/lib/styles/index.css';
import 'pdfjs-dist/build/pdf.worker.entry';
import { toast } from 'react-toastify';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  SelectChangeEvent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Loading } from '@bumble/app/components/Loading';

interface GeneralInfo {
  Date: string;
  "Device Name": string;
  "Product Name": string;
  "Device ID": string;
  "Description": string;
}

interface TestSuite {
  "Throughput Test": string;
  "Delay Test": string;
  "Frame Loss Test": string;
  "Back-to-back Test": string;
  "Strict Failure": string;
  "Verbose Report": string;
  "Protocol Used": string;
}

interface TestPeer {
  "Testing Layer": string;
  "Peer IP Address": string;
}

interface ThroughputTestEntry {
  "Frame Size": number;
  "Tx Rate (Mbps)": number;
  "Tx Rate (Frames/Sec)": number;
  "Rx Rate (Mbps)": number;
  "Rx Rate (Frames/sec)": number;
  "Frame Loss (%)": number;
  "Status": string;
}

interface LinkSpeedTestEntry {
  "Frame Size": number;
  "Tx Rate (Mbps)": number;
  "Tx Rate (Frames/Sec)": number;
  "Rx Rate (Mbps)": number;
  "Rx Rate (Frames/sec)": number;
}

interface DelayTestEntry {
  "Frame Size": number;
  "Minimum Delay (usec)": number;
  "Average Delay (usec)": number;
  "Maximum Delay (usec)": number;
  "Minimum DV (usec)": number;
  "Average DV (usec)": number;
  "Maximym DV (usec)": number;
  "Status": string;
}

interface ReportData {
  "Report Name": string;
  "General": GeneralInfo;
  "Test Suite": TestSuite;
  "Test Peer": TestPeer;
  "Throughput Test": ThroughputTestEntry[];
  "Link Speed Test": LinkSpeedTestEntry[];
  "Delay Test": DelayTestEntry[];
}

interface RouterDownloadResultProps {
  cpeId: string;
}

export const RouterDownloadResult = ({ cpeId }: RouterDownloadResultProps) => {
  const api = useApi();

  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [isPreview, setIsPreview] = useState<boolean>(false);
  const [pdfFile, setPdfFile] = useState<string | null>(null);
  const [doc, setDoc] = useState<jsPDF | null>(null);
  const [selectedReport, setSelectedReport] = useState<ReportData | null>(null);
  const [reports, setReports] = useState<string[]>([]);
  const [selectedReportName, setSelectedReportName] = useState<string>('');

  const toolbarPluginInstance = toolbarPlugin({});
  const { Toolbar } = toolbarPluginInstance; // Extract the Toolbar component

  const { isLoading, data: reportData } = useQuery(
    ['getCpeTestReportData'],
    api.getCpeTestReportApiV1GetCpeTestReportGet.bind(null, {
      cpe_id: cpeId
    }),
    { enabled: true, cacheTime: 0, initialData: undefined },
  );

  useEffect(() => {
    if (reportData != null && reportData != undefined) {
      setReports((reportData.data as any).map((item: ReportData) => item['Report Name']
      ));
    }
  }, [reportData]);

  const handleChange = ({ target: { value } }: SelectChangeEvent): void => {
    const foundReport = (reportData?.data as any).find(
      (item: ReportData) => item['Report Name'] === value
    );

    setSelectedReport(foundReport || null);
    setSelectedReportName(value);
  };

  const handleGenerate = async () => {
    if (selectedReport == null) {
      toast.error('There is no test data.');
      setIsPreview(false);
      setIsDownloading(false);
      return;
    };
    const doc = new jsPDF();

    const marginLeft = 20;
    const marginTop = 30;
    const marginRight = 20;
    const marginBottom = 20;
    const lineSpacing = 7;

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    // Define the usable width and height by subtracting margins
    const usableWidth = pageWidth - marginLeft - marginRight;
    const usableHeight = pageHeight - marginTop - marginBottom;
    let posy = marginTop;

    // Set title
    doc.setFontSize(24);
    // doc.setFont('serif', 'bold');
    doc.text(selectedReport['Report Name'], marginLeft, posy);

    posy += lineSpacing;
    doc.setLineWidth(1);
    doc.line(marginLeft, posy, pageWidth - marginRight, posy);

    // General Information
    posy += lineSpacing * 3;
    doc.setFontSize(20);
    doc.text('General Information', marginLeft, posy);
    posy += lineSpacing * 1.3;
    doc.setFontSize(12);
    Object.entries(selectedReport.General).forEach(([key, value], index) => {
      if (index != 0)
        posy += lineSpacing;
      doc.text(`${key}`, marginLeft, posy);
      doc.text(`: ${value}`, marginLeft + lineSpacing * 7, posy);
    });

    // Test Suite
    posy += lineSpacing * 3;
    doc.setFontSize(20);
    doc.text('Test Suite', marginLeft, posy);
    posy += lineSpacing * 1.3;
    doc.setFontSize(12);
    Object.entries(selectedReport['Test Suite']).forEach(([key, value], index) => {
      if (index != 0)
        posy += lineSpacing;
      doc.text(`${key}`, marginLeft, posy);
      doc.text(`: ${value}`, marginLeft + lineSpacing * 7, posy);
    });

    // Test Peer
    posy += lineSpacing * 3;
    doc.setFontSize(20);
    doc.text('Test Peer', marginLeft, posy);
    posy += lineSpacing * 1.3;
    doc.setFontSize(12);
    Object.entries(selectedReport['Test Peer']).forEach(([key, value], index) => {
      if (index != 0)
        posy += lineSpacing;
      doc.text(`${key}`, marginLeft, posy);
      doc.text(`: ${value}`, marginLeft + lineSpacing * 7, posy);
    });

    //Link Speed Test
    posy = marginTop;
    doc.setFontSize(20);
    doc.addPage();
    doc.text('Link Speed Test', marginLeft, posy);
    posy += lineSpacing * 1.3;

    (doc as any).autoTable({
      startY: posy,
      head: [
        [
          'Frame Size',
          'Tx Rate (Mbps)',
          'Tx Rate (Frame/Sec)',
          'Rx Rate (Mbps)',
          'Rx Rate (Frames/sec)'
        ]
      ],
      body: selectedReport['Link Speed Test'].map((test) => [
        test['Frame Size'],
        test['Tx Rate (Mbps)'],
        test['Tx Rate (Frames/Sec)'],
        test['Rx Rate (Mbps)'],
        test['Rx Rate (Frames/sec)']
      ]),
      alternateRowStyles: {
        fillColor: null // Set fillColor to null to remove stripes
      },
      styles: {
        // font: 'san-serif', // Set font (default is 'helvetica')
        fontStyle: 'normal', // Font style (e.g., 'normal', 'bold', 'italic')
        fontSize: 11, // Set font size
        halign: 'center', // Horizontal alignment (left, center, right)
        valign: 'middle', // Vertical alignment (top, middle, bottom)
        fillColor: [255, 255, 255], // Background color (white by default)
        textColor: [0, 0, 0], // Text color (black by default)
        lineColor: [0, 0, 0], // Border color
        lineWidth: 0, // Border width,
        cellPadding: { top: 5, bottom: 5, left: 1, right: 1 },
      },
      headStyles: {
        fillColor: [243, 243, 243], // Header background color (light blue)
        textColor: [0, 0, 0], // Header text color (white)
        fontStyle: 'normal', // Bold header text
        fontSize: 12,
        halign: 'center', // Header text alignment
      },
      didDrawCell: function (data: any) {
        if (data.section === 'body' || data.section === 'head') {
          doc.setDrawColor(0, 0, 0); // Black color for border
          doc.setLineWidth(0.2); // Thickness of bottom border
          doc.line(
            data.cell.x,
            data.cell.y + data.cell.height,
            data.cell.x + data.cell.width,
            data.cell.y + data.cell.height
          ); // Draw bottom border
        }
      },
      columnStyles: {
        0: { cellWidth: usableWidth / 5, cellHeight: lineSpacing * 1.3 },
        1: { cellWidth: usableWidth / 5 },
        2: { cellWidth: usableWidth / 5 },
        3: { cellWidth: usableWidth / 5 },
        4: { cellWidth: usableWidth / 5 },
      },
      margin: { left: marginLeft, right: marginRight },
      tableWidth: usableWidth,
    })

    // Throughput Test Table
    posy += 25 + 15 * selectedReport['Link Speed Test'].length + 20;
    doc.setFontSize(20);
    doc.text('Throughput Test', marginLeft, posy);
    posy += lineSpacing * 1.3;

    (doc as any).autoTable({
      startY: posy,
      head: [['Frame Size', 'Tx Rate(Mbps)', 'Tx Rate(Frames/Sec)',
        'Rx Rate(Mbps)', 'Rx Rate(Frames/sec)', 'Frame Loss(%)', 'Status']],
      body: selectedReport['Throughput Test'].map((test) => [
        test['Frame Size'],
        test['Tx Rate (Mbps)'],
        test['Tx Rate (Frames/Sec)'],
        test['Rx Rate (Mbps)'],
        test['Rx Rate (Frames/sec)'],
        test['Frame Loss (%)'],
        test['Status'],
      ]),
      styles: {
        // font: 'san-serif', // Set font (default is 'helvetica')
        fontStyle: 'normal', // Font style (e.g., 'normal', 'bold', 'italic')
        fontSize: 11, // Set font size
        halign: 'center', // Horizontal alignment (left, center, right)
        valign: 'middle', // Vertical alignment (top, middle, bottom)
        fillColor: [255, 255, 255], // Background color (white by default)
        textColor: [0, 0, 0], // Text color (black by default)
        lineColor: [0, 0, 0], // Border color
        lineWidth: 0, // Border width
        cellPadding: { top: 5, bottom: 5, left: 1, right: 1 }
      },
      rowStyles: {
        minCellHeight: lineSpacing * 1.3 // Controls the height of each row
      },
      headStyles: {
        fillColor: [243, 243, 243], // Header background color (light blue)
        textColor: [0, 0, 0], // Header text color (white)
        fontSize: 12,
        fontStyle: 'normal', // Bold header text
        halign: 'center' // Header text alignment
      },
      alternateRowStyles: {
        fillColor: null // Set fillColor to null to remove stripes
      },
      didDrawCell: function (data: any) {
        if (data.section === 'body' || data.section === 'head') {
          doc.setDrawColor(0, 0, 0); // Black color for border
          doc.setLineWidth(0.2); // Thickness of bottom border
          doc.line(
            data.cell.x,
            data.cell.y + data.cell.height,
            data.cell.x + data.cell.width,
            data.cell.y + data.cell.height
          ); // Draw bottom border
        }
      },
      columnStyles: {
        0: { cellWidth: usableWidth / 7 },
        1: { cellWidth: usableWidth / 7 },
        2: { cellWidth: usableWidth / 7 },
        3: { cellWidth: usableWidth / 7 },
        4: { cellWidth: usableWidth / 7 },
        5: { cellWidth: usableWidth / 7 },
        6: { cellWidth: usableWidth / 7 },
      },
      margin: { left: marginLeft, right: marginRight },
      tableWidth: usableWidth
    });

    // Delay Test Table
    posy = marginTop;
    doc.addPage();
    doc.setFontSize(20);
    doc.text('Delay Test', marginLeft, posy);
    posy += lineSpacing * 1.3;

    (doc as any).autoTable({
      startY: posy,
      head: [
        [
          'Frame Size',
          'Min Delay (usec)',
          'Avg Delay (usec)',
          'Max Delay (usec)',
          'Min DV (usec)',
          'Avg DV (usec)',
          'Max DV (usec)',
          'Status',
        ],
      ],
      body: selectedReport['Delay Test'].map((test) => [
        test['Frame Size'],
        test['Minimum Delay (usec)'],
        test['Average Delay (usec)'],
        test['Maximum Delay (usec)'],
        test['Minimum DV (usec)'],
        test['Average DV (usec)'],
        test['Maximym DV (usec)'],
        test['Status'],
      ]),
      alternateRowStyles: {
        fillColor: null // Set fillColor to null to remove stripes
      },
      styles: {
        // font: 'san-serif', // Set font (default is 'helvetica')
        fontStyle: 'normal', // Font style (e.g., 'normal', 'bold', 'italic')
        fontSize: 11, // Set font size
        halign: 'center', // Horizontal alignment (left, center, right)
        valign: 'middle', // Vertical alignment (top, middle, bottom)
        fillColor: [255, 255, 255], // Background color (white by default)
        textColor: [0, 0, 0], // Text color (black by default)
        lineColor: [0, 0, 0], // Border color
        lineWidth: 0, // Border width,
        cellPadding: { top: 5, bottom: 5, left: 1, right: 1 }
      },
      headStyles: {
        fillColor: [243, 243, 243], // Header background color (light blue)
        textColor: [0, 0, 0], // Header text color (white)
        fontStyle: 'normal', // Bold header text
        fontSize: 12,
        halign: 'center' // Header text alignment
      },
      didDrawCell: function (data: any) {
        if (data.section === 'body' || data.section === 'head') {
          doc.setDrawColor(0, 0, 0); // Black color for border
          doc.setLineWidth(0.2); // Thickness of bottom border
          doc.line(
            data.cell.x,
            data.cell.y + data.cell.height,
            data.cell.x + data.cell.width,
            data.cell.y + data.cell.height
          ); // Draw bottom border
        }
      },
      columnStyles: {
        0: { cellWidth: usableWidth / 8, cellHeight: lineSpacing * 1.3 },
        1: { cellWidth: usableWidth / 8 },
        2: { cellWidth: usableWidth / 8 },
        3: { cellWidth: usableWidth / 8 },
        4: { cellWidth: usableWidth / 8 },
        5: { cellWidth: usableWidth / 8 },
        6: { cellWidth: usableWidth / 8 },
        7: { cellWidth: usableWidth / 8 }
      },
      margin: { left: marginLeft, right: marginRight },
      tableWidth: usableWidth
    });

    setDoc(doc);
  };

  const handlePreview = () => {
    setIsPreview(true);
    handleGenerate();
  };

  const handleDownload = () => {
    setIsDownloading(true);
    if (doc) {
      doc.save(`${selectedReport?.['Report Name'] || 'Report'}.pdf`);
      setIsDownloading(false);
    } else {
      handleGenerate();
    }
  };

  useEffect(() => {
    if (doc != null) {
      if (isDownloading) {
        doc.save(`${selectedReport?.['Report Name'] || 'Report'}.pdf`);
        setIsDownloading(false);
      }
      if (isPreview) {
        setPdfFile(URL.createObjectURL(doc.output('blob')));
        setIsPreview(false);
      }
    }
  }, [doc]);

  if (isLoading) return <Loading />

  return (
    <Box pt={2}>
      <Typography variant="body1" sx={{ mb: 6 }}>
        Display the download the most recent test result.
      </Typography>
      <Box display="flex" justifyContent="center" sx={{ mb: 4 }}>
        <FormControl fullWidth>
          <InputLabel id="peer-region-select-label">Recent reports</InputLabel>
          <Select
            labelId="peer-region-select-label"
            value={selectedReportName}
            label="Recent reports"
            onChange={handleChange}
          >
            { reports && reports.map((item, i) => {
              return <MenuItem value={item} key={item}>{item}</MenuItem>;
            })}
          </Select>
        </FormControl>
      </Box>
      {pdfFile &&
        <Box sx={{ border: 1 }}>
          <div style={{ height: '60vh' }}>
            <Viewer fileUrl={pdfFile}
              plugins={[
                toolbarPluginInstance // Attach the toolbar plugin to the Viewer
              ]}
            />
            <div style={{ marginTop: '1rem' }}>
              <Toolbar>{(toolbarSlot) => (<div style={{ display: 'flex', justifyContent: 'center' }}>
                <div style={{ display: 'flex' }}>
                  <toolbarSlot.ZoomOut />
                  <toolbarSlot.ZoomIn />
                </div>
              </div>
              )}
              </Toolbar>
            </div>
          </div>
        </Box>
      }
      <Box display="flex" justifyContent="flex-end" sx={{ mt: 12 }}>
        {(isDownloading || isPreview) && <CircularProgress sx={{ mr: 5 }} />}
        <Button variant="outlined" onClick={handlePreview} disabled={isPreview || (selectedReport == null)} sx={{ mr: 5 }}>
          Preview
        </Button>
        <Button variant="outlined" onClick={handleDownload} disabled={isDownloading || (selectedReport == null)}>
          Download
        </Button>
      </Box>
    </Box>
  );
};
